import React, { useState, useEffect } from 'react';
import { 
  useEvent, 
  useLazyQuery, 
  useLazyCommand,
  useDomainEvent
} from "@soap/modules";
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Plus } from 'lucide-react';
import { QuestionWizard } from '../QuestionWizard';
import type { Assessment, Section, Question } from './types';
import { timingModeKeys } from './types';
import { calcPoints, reorder } from './utils';
import { AssessmentHelpNotifications } from './AssessmentHelpNotifications';
import { TimingModeSelector } from './TimingModeSelector';
import { AddSectionDialog } from './AddSectionDialog';
import { SectionItem } from './SectionItem';

export function EditAssessmentQuestions({ assessmentId }: { assessmentId: string }) {
  const [getAssessment, gotAssessment] = useLazyQuery("C188v1_GetAssessment");
  const [reorderTrigger] = useLazyCommand("C198v1_ReorderAssessmentItems");
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddSection, setShowAddSection] = useState(false);
  const [showQuestionWizard, setShowQuestionWizard] = useState(false);
  const [questionWizardSectionId, setQuestionWizardSectionId] = useState<string>('');
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const trigger = useDomainEvent({
    eventName: "timing-mode-changed",
    conversationId: "edit-assessment-questions"
  });

  useEffect(() => {
    if (assessmentId) {
      getAssessment({
        C188_AssessmentId: assessmentId
      });
    }
  }, [assessmentId]);

  useEvent({
    eventName: "E156v1_AssessmentSectionAdded",
    onEventReceived: (event: any) => {
      if (!assessment) return;
      const section: Section = {
        SectionId: event.e156_SectionId,
        Title: event.e156_SectionTitle,
        TimeLimit: event.e156_SectionTimeLimit,
        Points2: 0,
        QuestionCount: 0,
        Questions: []
      };
      const cloned = JSON.parse(JSON.stringify(assessment));
      cloned.Sections.push(section);
      calcPoints(cloned);
      setAssessment(cloned);
    }
  }, [assessment]);

  useEvent({
    eventName: "E266v1_EmbeddedQuestionAddedToAssessment",
    onEventReceived: (event: any) => {
      const eventAssessmentId = event.e266_AssessmentId || event.E266_AssessmentId;
      const eventSectionId = event.e266_SectionId || event.E266_SectionId;
      
      // Expand the section that received the question
      if (eventAssessmentId === assessmentId && eventSectionId) {
        setExpandedSections(prev => new Set(prev).add(eventSectionId));
      }
      
      // Refresh the assessment to get the complete question data (including QuestionTypeKey and QuestionData)
      if (assessmentId) {
        getAssessment({
          C188_AssessmentId: assessmentId
        });
      }
    }
  }, [assessmentId, getAssessment]);

  useEvent({
    eventName: "E267v1_EmbeddedQuestionEditedInAssessment",
    onEventReceived: (event: any) => {
      // Refresh the assessment to get the updated question data (including options)
      // This will update the entire assessment but only the edited question will have changed
      if (assessmentId) {
        getAssessment({
          C188_AssessmentId: assessmentId
        });
      }
    }
  }, [assessmentId, getAssessment]);

  useEvent({
    eventName: "E157v1_AssessmentSectionRemoved",
    onEventReceived: (event: any) => {
      if (!assessment) return;
      const cloned = JSON.parse(JSON.stringify(assessment));
      cloned.Sections = cloned.Sections.filter((s: Section) => s.SectionId !== event.e157_SectionId);
      calcPoints(cloned);
      setAssessment(cloned);
    }
  }, [assessment]);

  // Listen for timing updates
  useEvent({
    eventName: "E268v1_AssessmentTimingUpdated",
    onEventReceived: (event: any) => {
      const eventAssessmentId = event.e268_AssessmentId || event.E268_AssessmentId;
      if (eventAssessmentId !== assessmentId) return;
      
      // Update timing data directly from event
      if (assessment) {
        const cloned = JSON.parse(JSON.stringify(assessment));
        cloned.Version = event.e268_VersionId || event.E268_VersionId;
        
        // Update timing mode and overall time limit
        if (event.e268_TimingModeKey) {
          cloned.TimingMode = event.e268_TimingModeKey;
        }
        if (event.e268_OverallTimeLimitInSeconds !== undefined) {
          cloned.OverallTimeLimit = event.e268_OverallTimeLimitInSeconds;
        }
        
        // Update section time limits
        if (event.e268_Sections) {
          event.e268_Sections.forEach((sectionTiming: any) => {
            const section = cloned.Sections.find((s: Section) => s.SectionId === sectionTiming.e268_SectionId);
            if (section) {
              section.TimeLimit = sectionTiming.e268_TimeLimitInSeconds;
              
              // Update question time limits if provided (per-question mode)
              if (sectionTiming.e268_Questions) {
                sectionTiming.e268_Questions.forEach((qTiming: any) => {
                  const question = section.Questions.find((q: Question) => q.QuestionId === qTiming.e268_QuestionId);
                  if (question) {
                    question.TimeLimit2 = qTiming.e268_TimeLimitInSeconds;
                  }
                });
              }
            }
          });
        }
        
        setAssessment(cloned);
      }
    }
  }, [assessment, assessmentId]);

  // Listen for question removal event and remove the question directly from local state
  useEvent({
    eventName: "E160v1_QuestionRemovedFromAssessment",
    onEventReceived: (event: any) => {
      const eventAssessmentId = event.e160_AssessmentId || event.E160_AssessmentId;
      const eventQuestionId = event.e160_QuestionId || event.E160_QuestionId;
      
      // Only process if this is for our assessment
      if (eventAssessmentId === assessmentId && assessment && eventQuestionId) {
        const cloned = JSON.parse(JSON.stringify(assessment));
        const eventQuestionIdStr = String(eventQuestionId);
        
        // Find and remove the question from the appropriate section
        for (const section of cloned.Sections) {
          const questionIndex = section.Questions.findIndex(
            (q: Question) => String(q.QuestionId) === eventQuestionIdStr
          );
          if (questionIndex !== -1) {
            section.Questions.splice(questionIndex, 1);
            calcPoints(cloned);
            setAssessment(cloned);
            break;
          }
        }
      }
    }
  }, [assessment, assessmentId]);

  useEffect(() => {
    function convertE154(e154: any): Assessment {
      return {
        ShareCount: e154.e154_ShareCount,
        Points: 0,
        AssessmentId: assessmentId,
        OverallTimeLimit: e154.e154_AssessmentTimeLimitInSeconds,
        TimingMode: e154.e154_TimingModeKey,
        Version: e154.e154_Version,
        Sections: e154.e154_Sections.map((section: any) => ({
          TimeLimit: section.e154_SectionTimeLimitInSeconds,
          SectionId: section.e154_SectionId,
          Points2: section.e154_SectionPoints,
          Title: section.e154_SectionTitle,
          Questions: section.e154_Questions.map((question: any) => ({
            QuestionId: question.e154_QuestionId,
            Title: question.e154_QuestionTitle,
            QuestionText: question.e154_QuestionText,
            TimeLimit2: question.e154_QuestionTimeLimitInSeconds,
            Points3: question.e154_QuestionPoints,
            QuestionTypeKey: question.e154_QuestionTypeKey,
            QuestionData: question.e154_QuestionData
          }))
        }))
      };
    }

    if (gotAssessment) {
      const converted = convertE154(gotAssessment);
      calcPoints(converted);
      setAssessment(converted);
      setLoading(false);
    }
  }, [gotAssessment, assessmentId]);

  const handleTimingModeChange = (newMode: string) => {
    // Just trigger the change - timing data will be updated from E149 event
    trigger({ newTimingMode: newMode });
  };

  const onDragEnd = (result: any) => {
    if (!assessment || !result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    let updatedAssessment: Assessment | null = null;

    if (result.type === "droppableItem") {
      // Reordering sections
      const newSections = reorder(assessment.Sections, sourceIndex, destIndex);
      const cloned = JSON.parse(JSON.stringify(assessment));
      cloned.Sections = newSections;
      updatedAssessment = cloned;
      setAssessment(cloned);
    } else if (result.type === "droppableSubItem") {
      // Reordering questions within or between sections
      const sourceParentId = result.source.droppableId;
      const destParentId = result.destination.droppableId;
      
      const sourceSection = assessment.Sections.find(s => s.SectionId === sourceParentId);
      const destSection = assessment.Sections.find(s => s.SectionId === destParentId);
      
      if (!sourceSection || !destSection) return;

      const cloned = JSON.parse(JSON.stringify(assessment));
      const sourceSectionIndex = cloned.Sections.findIndex(s => s.SectionId === sourceParentId);
      const destSectionIndex = cloned.Sections.findIndex(s => s.SectionId === destParentId);

      if (sourceParentId === destParentId) {
        // Reordering within the same section
        const reorderedQuestions = reorder(
          cloned.Sections[sourceSectionIndex].Questions,
          sourceIndex,
          destIndex
        );
        cloned.Sections[sourceSectionIndex].Questions = reorderedQuestions;
      } else {
        // Moving between different sections
        const sourceQuestions = [...cloned.Sections[sourceSectionIndex].Questions];
        const destQuestions = [...cloned.Sections[destSectionIndex].Questions];
        
        const [draggedQuestion] = sourceQuestions.splice(sourceIndex, 1);
        destQuestions.splice(destIndex, 0, draggedQuestion);
        
        cloned.Sections[sourceSectionIndex].Questions = sourceQuestions;
        cloned.Sections[destSectionIndex].Questions = destQuestions;
      }
      
      updatedAssessment = cloned;
      setAssessment(cloned);
    }

    // Send reorder command to backend using the updated assessment
    if (updatedAssessment) {
      const orderedArrays = updatedAssessment.Sections.map(s => ({
        id: s.SectionId,
        questions: s.Questions.map(q => q.QuestionId)
      }));

      reorderTrigger({
        C198_AssessmentId: updatedAssessment.AssessmentId,
        C198_OrderedAssessmentItemArrays: JSON.stringify(orderedArrays)
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-action-blue"></div>
      </div>
    );
  }

  if (!assessment) return null;

  const existingQuestionIds = assessment.Sections.flatMap(s => s.Questions).map(q => q.QuestionId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Version:</span> {assessment.Version} 
          <span className="mx-3">•</span>
          <span className="font-medium">Total Points:</span> {assessment.Points}
        </div>
      </div>

      <AssessmentHelpNotifications
        sectionCount={assessment.Sections.length}
        questionCount={assessment.Sections.flatMap(x => x.Questions).length}
      />

      <TimingModeSelector 
        assessment={assessment} 
        onTimingModeChange={handleTimingModeChange}
      />

      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Sections</h2>
        <Button onClick={() => setShowAddSection(true)}>
          <Plus size={16} className="mr-2" /> Add Section
        </Button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" type="droppableItem">
          {(provided, snapshot) => (
            <div 
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={snapshot.isDraggingOver ? 'bg-action-blue/10 rounded-lg p-2' : ''}
            >
              {assessment.Sections.map((section, index) => (
                <SectionItem
                  key={section.SectionId}
                  section={section}
                  assessment={assessment}
                  onUpdate={setAssessment}
                  existingQuestionIds={existingQuestionIds}
                  index={index}
                  onDragEnd={onDragEnd}
                  expanded={expandedSections.has(section.SectionId)}
                  onExpandedChange={(expanded) => {
                    setExpandedSections(prev => {
                      const newSet = new Set(prev);
                      if (expanded) {
                        newSet.add(section.SectionId);
                      } else {
                        newSet.delete(section.SectionId);
                      }
                      return newSet;
                    });
                  }}
                  onShowQuestionWizard={(sectionId) => {
                    setShowQuestionWizard(true);
                    setQuestionWizardSectionId(sectionId);
                    setEditingQuestionId(null);
                  }}
                  onEditQuestion={(question, sectionId) => {
                    setShowQuestionWizard(true);
                    setQuestionWizardSectionId(sectionId);
                    setEditingQuestionId(question.QuestionId);
                  }}
                  onRefresh={() => {
                    if (assessmentId) {
                      getAssessment({
                        C188_AssessmentId: assessmentId
                      });
                    }
                  }}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {assessment.Sections.length === 0 && (
        <Card className="p-8 text-center text-gray-500">
          <p>No sections yet. Click "Add Section" to get started.</p>
        </Card>
      )}

      {showAddSection && (
        <AddSectionDialog
          assessmentId={assessment.AssessmentId}
          onClose={() => setShowAddSection(false)}
        />
      )}

      {showQuestionWizard && assessment && (() => {
        // Find the question being edited
        const editingQuestion = editingQuestionId 
          ? assessment.Sections
              .flatMap(s => s.Questions)
              .find(q => q.QuestionId === editingQuestionId)
          : null;
        
        // Convert question data to wizard format
        const initialData = editingQuestion ? (() => {
          const questionData = editingQuestion.QuestionData;
          const questionType = editingQuestion.QuestionTypeKey as any;
          
          let options: any[] = [];
          // Handle different property name cases from server (e154_PossibleAnswers or E154_PossibleAnswers)
          const possibleAnswers = questionData?.e154_PossibleAnswers || questionData?.E154_PossibleAnswers;
          
          if (possibleAnswers && Array.isArray(possibleAnswers)) {
            options = possibleAnswers.map((ans: any, idx: number) => {
              // Handle different enumeration property name cases
              const enumeration = ans.e154_Enumeration || ans.E154_Enumeration || ans.Enumeration || ans.enumeration;
              // Handle different key/value property cases (camelCase vs PascalCase)
              const key = enumeration?.key || enumeration?.Key || ans.key || ans.Key || `option_${idx}`;
              const value = enumeration?.value || enumeration?.Value || ans.value || ans.Value || '';
              // Handle different correct answer property cases
              const correctKeys = questionData.e154_CorrectAnswerKeys || questionData.E154_CorrectAnswerKeys || [];
              const correctKey = questionData.e154_CorrectAnswerKey || questionData.E154_CorrectAnswerKey;
              const isCorrect = correctKeys.includes(key) || correctKey === key;
              return { key, value, isCorrect };
            });
          }
          
          // Extract media fields - check all possible property name cases
          const rawMediaItem = questionData?.e154_MediaItem || questionData?.E154_MediaItem || questionData?.MediaItem || questionData?.mediaItem;
          const mediaCaption = questionData?.e154_MediaItemCaption || questionData?.E154_MediaItemCaption || questionData?.MediaItemCaption || questionData?.mediaItemCaption;
          
          const mediaItem = rawMediaItem;
          
          return {
            questionText: editingQuestion.QuestionText,
            questionType: questionType,
            points: editingQuestion.Points3 || 10,
            timeLimit: editingQuestion.TimeLimit2,
            options: options,
            charLimit: questionData?.e154_CharLimit || questionData?.E154_CharLimit || 500,
            allowPartialCredit: questionData?.e154_AllowPartialCredit || questionData?.E154_AllowPartialCredit || false,
            mediaItem: mediaItem ? { 
              id: mediaItem.id || mediaItem.Id || mediaItem.ID, 
              name: mediaItem.name || mediaItem.Name || mediaItem.NAME || 'image.jpg' 
            } : null,
            mediaCaption: mediaCaption || ''
          };
        })() : undefined;
        
        // Find the section being edited
        const currentSection = assessment.Sections.find(s => s.SectionId === questionWizardSectionId);
        const sectionTimeLimit = currentSection?.TimeLimit;
        const sectionQuestionCount = currentSection?.Questions?.length || 0;
        const overallTimeLimit = assessment.OverallTimeLimit;
        const totalQuestionCount = assessment.Sections.flatMap(s => s.Questions || []).length;

        return (
          <QuestionWizard
            open={showQuestionWizard}
            onClose={() => {
              setShowQuestionWizard(false);
              setQuestionWizardSectionId('');
              setEditingQuestionId(null);
            }}
            assessmentId={assessment.AssessmentId}
            sectionId={questionWizardSectionId}
            editingQuestionId={editingQuestionId || undefined}
            timingMode={assessment.TimingMode}
            sectionTimeLimit={sectionTimeLimit}
            sectionQuestionCount={sectionQuestionCount}
            overallTimeLimit={overallTimeLimit}
            totalQuestionCount={totalQuestionCount}
            initialQuestionData={initialData}
          />
        );
      })()}
    </div>
  );
}

